/**
 * SPEC 2026-10-26 Story 1 — Settings → Operations → Required vaccinations.
 *
 * GET/POST/PUT/DELETE /api/v2/admin/tenant/vaccination-requirements (+
 * /preview), on the same `/api/v2/admin/tenant` router as the operations
 * card. Read is business-config:read:all (owner / manager / receptionist /
 * accountant); writes are anyOf business-config:write:all (owner, manager) or
 * business-config.vaccinations:write:all (owner + manager — D1).
 *
 * Errors are `{ code, error }` with `error` already the owner's sentence;
 * `request` keeps both. An older server answers 403 `route_not_mapped`
 * (`isRouteNotMapped`). Every 200 body is PARSED here (parse, don't
 * validate): a body that is not the view becomes an error the section shows
 * honestly, never a crash and never an empty list that reads "none required".
 */
import { z } from 'zod';
import { api, type ApiResponse } from './api.js';

const coverageSchema = z.object({ current: z.number(), onFile: z.number() });
const serviceSchema = z.object({ id: z.string(), name: z.string() });

const requirementSchema = z.object({
  /** `rabies`, `dhpp`… or `other:giardia` for an Other vaccine. Never shown. */
  vaccineId: z.string(),
  name: z.string(),
  kind: z.enum(['catalog', 'custom']),
  hint: z.string().nullable(),
  /** Comparison keys of every record name that counts (lower case: "dhpp", "kennel cough"). */
  accepts: z.array(z.string()),
  /** The owner's note to customers; null when none. */
  description: z.string().nullable(),
  gracePeriodDays: z.number().int(),
  appliesTo: z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('all') }),
    z.object({ kind: z.literal('services'), services: z.array(serviceSchema) }),
  ]),
  coverage: coverageSchema,
});

const catalogOptionSchema = z.object({
  key: z.string(),
  name: z.string(),
  hint: z.string().nullable(),
  accepts: z.array(z.string()),
  alreadyRequired: z.boolean(),
  /** As if required with no grace period — the preview before saving. */
  coverage: coverageSchema,
});

export const vaccinationRequirementsViewSchema = z.object({
  requirements: z.array(requirementSchema),
  catalog: z.array(catalogOptionSchema),
  /** This shop's active services a customer can book. */
  services: z.array(serviceSchema),
  /** Record names on file that count toward nothing the shop could require. */
  unrecognizedRecordNames: z.array(z.object({ name: z.string(), dogs: z.number() })).default([]),
  lastChange: z.object({ at: z.string(), byName: z.string() }).nullable().default(null),
  limits: z
    .object({ maxGracePeriodDays: z.number(), maxNoteLength: z.number(), maxNameLength: z.number() })
    .default({ maxGracePeriodDays: 90, maxNoteLength: 200, maxNameLength: 60 }),
});

const previewSchema = z.object({ catalogName: z.string().nullable(), coverage: coverageSchema });

/** "312 of 340 dogs on file have a current Rabies record" — the gate's own rule, run today. */
export type VaccineCoverage = z.infer<typeof coverageSchema>;
export type VaccinationRequirementService = z.infer<typeof serviceSchema>;
export type VaccinationRequirementView = z.infer<typeof requirementSchema>;
export type VaccineCatalogOption = z.infer<typeof catalogOptionSchema>;
export type VaccinationRequirementsView = z.infer<typeof vaccinationRequirementsViewSchema>;
/** The catalog vaccine an "Other" name already is ("dapp" → "DHPP"), else null; and its coverage. */
export type VaccineNamePreview = z.infer<typeof previewSchema>;

export type VaccineChoiceInput = { kind: 'catalog'; key: string } | { kind: 'other'; name: string };
export type VaccinationAppliesToInput = { kind: 'all' } | { kind: 'services'; serviceTypeIds: string[] };

export interface CreateVaccinationRequirementInput {
  vaccine: VaccineChoiceInput;
  appliesTo: VaccinationAppliesToInput;
  gracePeriodDays: number;
  description: string | null;
}

/** An edit replaces the vaccine's whole rule; the vaccine itself is fixed. */
export interface UpdateVaccinationRequirementInput {
  appliesTo: VaccinationAppliesToInput;
  gracePeriodDays: number;
  description: string | null;
}

const PATH = '/api/v2/admin/tenant/vaccination-requirements';

const UNREADABLE = "We couldn't read the vaccination list from the server. Try again in a moment.";

/** A 200 body that is not the shape becomes an honest error; every failure passes through as-is. */
function parsed<T>(res: ApiResponse<unknown>, schema: z.ZodType<T>): ApiResponse<T> {
  if (res.data === undefined) {
    const { data: _data, ...failure } = res;
    return failure as ApiResponse<T>;
  }
  const result = schema.safeParse(res.data);
  return result.success ? { data: result.data } : { error: UNREADABLE };
}

export const adminVaccinationRequirementsApi = {
  get: async (apiUrl: string): Promise<ApiResponse<VaccinationRequirementsView>> =>
    parsed(await api.get<unknown>(apiUrl, PATH), vaccinationRequirementsViewSchema),
  preview: async (apiUrl: string, name: string): Promise<ApiResponse<VaccineNamePreview>> =>
    parsed(await api.get<unknown>(apiUrl, `${PATH}/preview?name=${encodeURIComponent(name)}`), previewSchema),
  create: async (
    apiUrl: string,
    body: CreateVaccinationRequirementInput,
  ): Promise<ApiResponse<VaccinationRequirementsView>> =>
    parsed(await api.post<unknown>(apiUrl, PATH, body), vaccinationRequirementsViewSchema),
  update: async (
    apiUrl: string,
    vaccineId: string,
    body: UpdateVaccinationRequirementInput,
  ): Promise<ApiResponse<VaccinationRequirementsView>> =>
    parsed(
      await api.put<unknown>(apiUrl, `${PATH}/${encodeURIComponent(vaccineId)}`, body),
      vaccinationRequirementsViewSchema,
    ),
  remove: async (apiUrl: string, vaccineId: string): Promise<ApiResponse<VaccinationRequirementsView>> =>
    parsed(await api.delete<unknown>(apiUrl, `${PATH}/${encodeURIComponent(vaccineId)}`), vaccinationRequirementsViewSchema),
};
